// OpenAI Ads attribution for signups.
//
// A visitor from a ChatGPT ad lands on www.pricerest.com with ?oppref=...; the
// OpenAI Pixel installed there stores it in the first-party `__oppref` cookie
// on .pricerest.com (and a browser reference in `__obref`), so this app can
// read both. An ad that links straight to the app is covered by reading the
// URL as well. The references are only forwarded to our backend with the
// signup request; the backend reports the conversion once the account exists.
//
// Consent: revoking measurement consent on the marketing site makes the Pixel
// delete both cookies, and nothing is read once they are gone.

const SESSION_KEY = 'openai_oppref';
const REFERENCE_PATTERN = /^[\x21-\x7e]{1,512}$/;

const cleanReference = (value) => {
    if (typeof value !== 'string')
        return null;
    const trimmed = value.trim();
    return REFERENCE_PATTERN.test(trimmed) ? trimmed : null;
};

// HashRouter URLs carry the query in one of two places:
// app.pricerest.com/?oppref=x#/register or app.pricerest.com/#/register?oppref=x
export const readOpprefFromUrl = (location = window.location) => {
    const fromSearch = new URLSearchParams(location.search || '').get('oppref');
    const hash = location.hash || '';
    const queryStart = hash.indexOf('?');
    const fromHash = queryStart >= 0 ? new URLSearchParams(hash.slice(queryStart + 1)).get('oppref') : null;
    return cleanReference(fromSearch) || cleanReference(fromHash);
};

export const readCookie = (name, cookieString = document.cookie) => {
    const prefix = name + '=';
    const match = (cookieString || '').split(';').map(part => part.trim()).find(part => part.startsWith(prefix));
    if (!match)
        return null;
    try {
        return decodeURIComponent(match.slice(prefix.length));
    } catch (e) {
        return null;
    }
};

const readSession = () => {
    try {
        return cleanReference(window.sessionStorage.getItem(SESSION_KEY));
    } catch (e) {
        return null;
    }
};

// Called on app load so a reference in the landing URL survives in-app
// navigation (e.g. landing on /login, then moving to /register).
export const captureOpenAIAttribution = () => {
    const oppref = readOpprefFromUrl();
    if (!oppref)
        return;
    try {
        window.sessionStorage.setItem(SESSION_KEY, oppref);
    } catch (e) {
        // Storage blocked: the URL and the Pixel cookie remain as sources.
    }
};

// The references to send with a signup. Empty object when there are none.
export const getOpenAIAttribution = () => {
    const attribution = {};
    const oppref = readOpprefFromUrl() || readSession() || cleanReference(readCookie('__oppref'));
    const obref = cleanReference(readCookie('__obref'));
    if (oppref)
        attribution.oppref = oppref;
    if (obref)
        attribution.obref = obref;
    return attribution;
};

// Spreads into a request payload; adds nothing when there is no attribution.
export const withOpenAIAttribution = (payload) => {
    const attribution = getOpenAIAttribution();
    return Object.keys(attribution).length ? {...payload, attribution} : payload;
};
